import { AuthenticatedTemplate } from '@azure/msal-react';
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { faPencil, faCheck, faMagnifyingGlass, faCopy, faSnowflake } from "@fortawesome/free-solid-svg-icons";
import { Login } from '../../../Login';
import Container from "../../common/layout/Container";
import Columns from "../../common/layout/Columns";
import Column from "../../common/layout/Column";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import StateHandler from "../../common/elements/StateHandler";
import InfoCard from "../../common/elements/InfoCard";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { ThorProvider, useThor } from "../context/ThorContext";
import BucketsTab from "../components/BucketsTab";
import AudiencesTab from "../components/AudiencesTab";
import DetailsTab from "../components/DetailsTab";
import MetricsTab from "../components/MetricsTab";
import SegmentsTab from "../components/SegmentsTab";
import ApprovalsTab from "../components/ApprovalsTab";
import RunExperimentTab from "../components/RunExperimentTab";
import OutcomeTab from "../components/OutcomeTab";
import VariablesTab from "../components/VariablesTab";
import OverridesTab from "../components/OverridesTab";
import ScheduleTab from "../components/ScheduleTab";
import AnalysisTab from "../components/AnalysisTab";
import BucketDecisionsTab from "../components/BucketDecisionsTab";
import ChangeLogTab from "../components/ChangeLogTab";
import CreateExperimentForm from "../components/CreateExperimentForm";
import ExperimentSearch from "../components/ExperimentSearch";
import ExperimentChecklist from "../components/ExperimentChecklist";
import StateChip from "../components/StateChip";
import ThorToast from "../components/ThorToast";
import { getProjectName } from "../utils/thorProjects";
import ValidationChip from "../components/ValidationChip";
import { serverErrMsg } from "../utils/validators";
import "../css/thor.css";

// ── Inline tooltip wrapper (hover over any child element) ─────────────────

const TOOLTIP_STYLE = {
    position: 'absolute',
    zIndex: 200,
    background: '#363636',
    color: '#fff',
    fontSize: '0.75rem',
    lineHeight: 1.45,
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    whiteSpace: 'pre-line',
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    pointerEvents: 'none',
    top: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    minWidth: '220px',
};

const TagTooltip = ({ children, text }) => {
    const [visible, setVisible] = useState(false);
    return (
        <span style={{ position: 'relative', display: 'inline-block' }}
              onMouseEnter={() => setVisible(true)}
              onMouseLeave={() => setVisible(false)}>
            {children}
            {visible && <span style={TOOLTIP_STYLE}>{text}</span>}
        </span>
    );
};

// ── Sidebar nav structure ──────────────────────────────────────────────────

const NAV_GROUPS = [
    {
        label: 'DESIGN',
        items: [
            { id: 'overview',   label: 'Overview' },
            { id: 'hypothesis', label: 'Hypothesis' },
            { id: 'config',     label: 'Configuration' },
            { id: 'stakeholders', label: 'Stakeholders' },
        ],
    },
    {
        label: 'BUILD',
        items: [
            { id: 'buckets',         label: 'Buckets' },
            { id: 'audiences',       label: 'Audiences' },
            { id: 'variables',       label: 'Variables' },
            { id: 'overrides',       label: 'Overrides' },
            { id: 'instrumentation', label: 'Instrumentation' },
        ],
    },
    {
        label: 'APPROVALS',
        items: [
            { id: 'approvals', label: 'Approvals' },
        ],
    },
    {
        label: 'RUN',
        items: [
            { id: 'schedule', label: 'Schedule' },
            { id: 'run',      label: 'Run Experiment' },
        ],
    },
    {
        label: 'ANALYSIS',
        items: [
            { id: 'metrics',        label: 'Metrics' },
            { id: 'segments',       label: 'Segments' },
            { id: 'bucketDecisions', label: 'Bucket Decisions', hint: "Live bucketing decisions from Snowflake — who's being bucketed, and why some aren't" },
            { id: 'analysis',       label: 'Analysis' },
            { id: 'outcome',        label: 'Outcome' },
            { id: 'changelog',      label: 'Change Log' },
        ],
    },
];

const DETAIL_SECTIONS = new Set(['overview', 'hypothesis', 'config', 'stakeholders', 'instrumentation']);
const ALL_ITEMS = NAV_GROUPS.flatMap(g => g.items);

const ComingSoon = ({ label }) => (
    <InfoCard title={`${label} — coming soon`} />
);

// ── Main content ───────────────────────────────────────────────────────────

const ThorContent = () => {
    const { execute } = useFetchWithMsal();
    const { editMode, setEditMode, addToast } = useThor();
    const [searchParams, setSearchParams] = useSearchParams();

    const [experimentsState, setExperimentsState] = useState(buildLoadingState([]));
    const [experiment, setExperiment] = useState(null);
    const [searching, setSearching] = useState(false);
    const [activeNav, setActiveNav] = useState(searchParams.get('tab') || 'overview');
    const [urlAutoSelected, setUrlAutoSelected] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [createMode, setCreateMode] = useState('blank');
    const [isArchiving, setIsArchiving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [checklistDoneSet, setChecklistDoneSet] = useState(null);
    const layoutRef = useRef(null);

    // Map checklist checkIds → sidebar item ids for dot computation
    const SIDEBAR_DOTS = {
        overview: ['overview'],
        hypothesis: ['hypothesis_core', 'strategic_goal', 'background_research'],
        config: ['days_to_power', 'start_date'],
        buckets: ['buckets'],
        metrics: ['metrics', 'guardrail_metrics'],
        audiences: ['audience_desc'],
        stakeholders: ['stakeholders'],

        segments: ['segments'],
        variables: ['variables'],
        outcome: ['outcome_conclusions'],
    };

    // Only the approval checkIds relevant to each state
    const STATE_APPROVAL_KEYS = {
        design_review: ['analyst_review', 'engineer_review'],
        instrumentation: ['instrumentation_signoff'],
        pre_prod_validation: ['pre_prod_signoff'],
        cleanup: ['cleanup_signoff'],
    };

    // Optional tabs only show ✓ when done — never ○/◐, which imply "missing required thing"
    const OPTIONAL_SIDEBAR = new Set(['variables', 'segments']);

    const getSidebarDot = (itemId) => {
        if (!checklistDoneSet) return null;

        if (itemId === 'approvals') {
            const expState = experiment?.state;
            const approvalKeys = STATE_APPROVAL_KEYS[expState];
            if (approvalKeys) {
                const done = approvalKeys.every(k => checklistDoneSet.has(k));
                const any = approvalKeys.some(k => checklistDoneSet.has(k));
                if (done) return 'done';
                if (any) return 'partial';
                return 'empty';
            }
            // States with no active approvals — show ✓ if all prior approval phases are past
            const allPriorApprovals = ['analyst_review', 'engineer_review', 'instrumentation_signoff', 'pre_prod_signoff'];
            if (allPriorApprovals.every(k => checklistDoneSet.has(k))) return 'done';
            return null;
        }

        const keys = SIDEBAR_DOTS[itemId];
        if (!keys) return null;
        const done = keys.every(k => checklistDoneSet.has(k));
        if (OPTIONAL_SIDEBAR.has(itemId)) return done ? 'done' : null;
        const any = keys.some(k => checklistDoneSet.has(k));
        if (done) return 'done';
        if (any) return 'partial';
        return 'empty';
    };

    // Update document title to show experiment name when viewing
    useEffect(() => {
        const prev = document.title;
        if (experiment && !searching) {
            document.title = `${experiment.name ?? experiment.key} — Thor`;
        } else {
            document.title = 'Thor';
        }
        return () => { document.title = prev; };
    }, [experiment, searching]);

    useEffect(() => {
        if (layoutRef.current) {
            layoutRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [activeNav]);

    useEffect(() => {
        execute('GET', thor_api_urls.listAllExperiments)
            .then(data => setExperimentsState(buildDataState(data?.experiments ?? [])))
            .catch(error => {
                console.error("Failed to fetch Thor experiments:", error);
                setExperimentsState(buildErrorState(error, []));
            });
    }, [execute]);

    // ── Initial URL restore (runs once when experiments finish loading) ────────
    useEffect(() => {
        const experiments = experimentsState.data;
        if (urlAutoSelected || !experiments?.length) return;
        setUrlAutoSelected(true);
        const key = searchParams.get('experiment');
        const tab = searchParams.get('tab');
        if (key) {
            const exp = experiments.find(e => e.key === key);
            if (exp) {
                setExperiment(exp);
                setSearching(false);
                if (tab && ALL_ITEMS.some(i => i.id === tab)) setActiveNav(tab);
            } else {
                addToast(`Experiment "${key}" not found.`, 'warning');
                setSearchParams({});
            }
        }
    }, [experimentsState.data, urlAutoSelected]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── URL → state sync (handles browser back/forward after initial load) ────
    const experimentsDataRef = useRef(null);
    useEffect(() => { experimentsDataRef.current = experimentsState.data; }, [experimentsState.data]);

    useEffect(() => {
        if (!urlAutoSelected) return; // Wait for initial load first

        const urlExp = searchParams.get('experiment');
        const urlTab = searchParams.get('tab');

        if (!urlExp) {
            // Back to search (URL has no experiment param)
            setExperiment(prev => { if (prev) { setEditMode(true); setIsDirty(false); } return null; });
            setSearching(false);
            return;
        }

        // Experiment changed (e.g., forward nav to a different experiment)
        setExperiment(prev => {
            if (prev?.key === urlExp) return prev;
            return experimentsDataRef.current?.find(e => e.key === urlExp) ?? prev;
        });
        setSearching(false);

        // Tab changed via back/forward
        if (urlTab && ALL_ITEMS.some(i => i.id === urlTab)) {
            setActiveNav(urlTab);
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const guardedNavigate = (tab) => {
        if (isDirty) {
            if (window.confirm('You have unsaved changes. Leave without saving?')) {
                setIsDirty(false);
                setActiveNav(tab);
                setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', tab); return p; });
            }
        } else {
            setActiveNav(tab);
            setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', tab); return p; });
        }
    };

    const handleSelect = (exp) => {
        setExperiment(exp);
        setSearching(false);
        setShowCreate(false);
        setEditMode(true);
        setIsDirty(false);
        setActiveNav('overview');
        setSearchParams({ experiment: exp.key, tab: 'overview' });
    };

    const refreshExperiments = () =>
        execute('GET', thor_api_urls.listAllExperiments)
            .then(data => setExperimentsState(buildDataState(data?.experiments ?? [])));

    const handleArchive = () => {
        setIsArchiving(true);
        execute('DELETE', thor_api_urls.experiment(experiment.key))
            .then(() => {
                addToast(`Experiment "${experiment.name ?? experiment.key}" archived.`, 'success');
                setExperiment(null);
                setSearching(false);
                setIsDirty(false);
                setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('experiment'); p.delete('tab'); return p; });
                refreshExperiments();
            })
            .catch(err => {
                addToast(serverErrMsg(err, 'Failed to archive experiment.'), 'danger');
            })
            .finally(() => setIsArchiving(false));
    };

    // Fix 7 — Auto-enable edit + navigate to hypothesis on first experiment creation
    const handleCreated = (exp) => {
        refreshExperiments();
        handleSelect(exp);
        // After handleSelect sets editMode false, re-enable it and go to hypothesis
        setEditMode(true);
        setActiveNav('hypothesis');
    };

    const renderSection = () => {
        if (DETAIL_SECTIONS.has(activeNav))
            return <DetailsTab key={experiment.key} experimentKey={experiment.key} section={activeNav}
                               onArchive={handleArchive} isArchiving={isArchiving} isArchived={!!experiment.archived}
                               onDirtyChange={setIsDirty}
                               onSave={updates => setExperiment(e => ({ ...e, ...updates }))}
                               onMetaChange={meta => setExperiment(e => ({ ...e, ...meta }))} />;
        switch (activeNav) {
            case 'buckets':   return <BucketsTab   key={experiment.key} experimentKey={experiment.key} />;
            case 'audiences': return <AudiencesTab key={experiment.key} experimentKey={experiment.key} />;
            case 'metrics':   return <MetricsTab   key={experiment.key} experimentKey={experiment.key} />;
            case 'segments':  return <SegmentsTab  key={experiment.key} experimentKey={experiment.key} />;
            case 'variables':  return <VariablesTab  key={experiment.key} experimentKey={experiment.key} />;
            case 'overrides':  return <OverridesTab  key={experiment.key} experimentKey={experiment.key} />;
            case 'schedule':   return <ScheduleTab   key={experiment.key} experimentKey={experiment.key} />;
            case 'bucketDecisions': return <BucketDecisionsTab key={experiment.key} experimentKey={experiment.key} />;
            case 'analysis':   return <AnalysisTab   key={experiment.key} experimentKey={experiment.key} />;
            case 'changelog':  return <ChangeLogTab  key={experiment.key} experimentKey={experiment.key} />;
            case 'approvals': return <ApprovalsTab    key={experiment.key} experimentKey={experiment.key} experimentState={experiment.state} onNavigate={guardedNavigate} onStateChange={newState => setExperiment(e => ({ ...e, state: newState }))} />;
            case 'run':       return <RunExperimentTab key={experiment.key} experimentKey={experiment.key} onNavigate={guardedNavigate} onStateChange={newState => setExperiment(e => ({ ...e, state: newState }))} />;
            case 'outcome':   return <OutcomeTab       key={experiment.key} experimentKey={experiment.key} experimentState={experiment.state} />;
            default:
                const label = ALL_ITEMS.find(i => i.id === activeNav)?.label ?? activeNav;
                return <ComingSoon label={label} />;
        }
    };

    return (
        <>
            <Container>
                {/* Compact page header */}
                <div className="level mb-0 mt-4">
                    <div className="level-left">
                        <p className="title is-4 mb-0">Thor</p>
                    </div>
                </div>

                {/* Top-level navigation */}
                <div className="tabs mb-3 mt-2">
                    <ul>
                        <li className="is-active"><Link to="/thor/experiments">Experiments</Link></li>
                        <li><Link to="/thor/audiences">Audience Library</Link></li>
                        <li><Link to="/thor/exclusion-groups">Exclusion Groups</Link></li>
                        <li><Link to="/thor/resurrector">Resurrector</Link></li>
                    </ul>
                </div>

                <StateHandler showLoader={experimentsState.loading} showError={experimentsState.error}
                              loadingScreen={<SmallLoadingSpinner />}>
                    {!experiment || searching ? (
                        <Columns>
                            <Column style={{ minWidth: 0 }}>
                                {showCreate ? (
                                    <CreateExperimentForm
                                        mode={createMode}
                                        experiments={experimentsState.data ?? []}
                                        onCreated={handleCreated}
                                        onCancel={() => setShowCreate(false)}
                                    />
                                ) : (
                                    <>
                                        <div className="level mb-3">
                                            <div className="level-left" />
                                            <div className="level-right" style={{ gap: '0.5rem', display: 'flex' }}>
                                                <Button className="is-link is-small" onClick={() => { setCreateMode('blank'); setShowCreate(true); }}>
                                                    + Create Experiment
                                                </Button>
                                                <Button className="is-link is-light is-small" onClick={() => { setCreateMode('clone'); setShowCreate(true); }}>
                                                    <Icon icon={faCopy} />
                                                    <span>Create from Existing</span>
                                                </Button>
                                            </div>
                                        </div>
                                        <ExperimentSearch
                                            experiments={experimentsState.data ?? []}
                                            onSelect={handleSelect}
                                        />
                                    </>
                                )}
                            </Column>
                        </Columns>
                    ) : (
                        <>
                            {/* Compact experiment header */}
                            <div className="box mb-0" style={{ borderRadius: '6px 6px 0 0' }}>
                                <div className="level mb-0">
                                    <div className="level-left">
                                        <div>
                                            <p className="title is-5 mb-1">{experiment.name ?? experiment.key}</p>
                                            <div style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <span className="tag is-dark is-small">{experiment.key}</span>
                                                {/* Fix 8 — Replace clipboard emoji with FontAwesome icon */}
                                                <button
                                                    type="button"
                                                    className="button is-small is-light"
                                                    title="Copy experiment key"
                                                    style={{ padding: '0 0.5rem', height: '1.5rem', fontSize: '0.75rem' }}
                                                    onClick={() => { navigator.clipboard.writeText(experiment.key); addToast('Key copied!', 'info'); }}
                                                >
                                                    <Icon icon={faCopy} />
                                                </button>
                                                <StateChip state={experiment.state} />
                                                {experiment.projectId != null && (
                                                    <span className="tag is-light is-small">{getProjectName(experiment.projectId)}</span>
                                                )}
                                                {experiment.trafficAllocation != null && (() => {
                                                    const configured = experiment.trafficAllocation;
                                                    const actual = experiment.actualTrafficAllocation;
                                                    const differs = actual != null && actual !== configured;
                                                    const tag = (
                                                        <span className={`tag is-small ${differs ? 'is-warning' : 'is-info is-light'}`}>
                                                            {configured}%{differs ? `→${actual}%` : ''} traffic{differs ? ' ⚠' : ''}
                                                        </span>
                                                    );
                                                    return differs
                                                        ? <TagTooltip text={`Configured: ${configured}%\nActual effective: ${actual}% (reduced by exclusion group)`}>{tag}</TagTooltip>
                                                        : tag;
                                                })()}
                                                <ValidationChip key={experiment.key} experimentKey={experiment.key} onNavigate={guardedNavigate} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="level-right" style={{ gap: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                        {/* Always-visible entry point so Bucket Decisions is discoverable from any tab */}
                                        <Button className="is-small is-info"
                                                onClick={() => {
                                                    guardedNavigate('bucketDecisions');
                                                    // Also scroll explicitly: if we're already on this tab, activeNav
                                                    // doesn't change so the [activeNav] scroll effect won't fire.
                                                    layoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }}
                                                title="See live bucketing decisions from Snowflake — who's being bucketed, and why some aren't">
                                            <Icon icon={faSnowflake} />
                                            <span>Live Bucketing</span>
                                        </Button>
                                        {/* Fix 2 — Renamed Edit button labels */}
                                        <Button
                                            className={`is-small ${editMode ? 'is-warning' : 'is-light'}`}
                                            onClick={() => {
                                                if (editMode && isDirty) {
                                                    if (!window.confirm('You have unsaved changes. Leave without saving?')) return;
                                                }
                                                setIsDirty(false);
                                                setEditMode(!editMode);
                                            }}
                                        >
                                            <Icon icon={editMode ? faCheck : faPencil} />
                                            <span>{editMode ? 'Done Editing' : 'Enable Editing'}</span>
                                        </Button>
                                        {experiment.archived && (
                                            <span className="tag is-dark is-small">Archived</span>
                                        )}
                                        {/* Fix 3 — Rename "Change" to "Switch Experiment" with icon */}
                                        <Button className="is-small is-light" onClick={() => { setSearching(true); setExperiment(null); setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('experiment'); p.delete('tab'); return p; }); refreshExperiments(); }}>
                                            <Icon icon={faMagnifyingGlass} />
                                            <span>Switch Experiment</span>
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Fix 1 — Edit mode persistent banner */}
                            {editMode ? (
                                <div className="notification is-warning is-light py-2 px-4 mb-3" style={{ borderLeft: '4px solid #ffdd57', borderRadius: 0, marginTop: 0 }}>
                                    ✏️ Editing mode — changes save immediately. Click <strong>Done Editing</strong> to exit.
                                </div>
                            ) : (
                                <div className="notification is-light py-2 px-4 mb-3" style={{ borderLeft: '4px solid #dbdbdb', borderRadius: 0, marginTop: 0 }}>
                                    🔒 Read-only — click <strong>Enable Editing</strong> to make changes.
                                </div>
                            )}

                            {/* Checklist — per-state next-step guide */}
                            <ExperimentChecklist
                                state={experiment.state}
                                experimentKey={experiment.key}
                                onNavigate={guardedNavigate}
                                onDoneSetChange={setChecklistDoneSet}
                            />

                            {/* Sidebar + content */}
                            <div className="thor-layout" ref={layoutRef}>
                                <aside className="thor-sidebar">
                                    <nav className="menu">
                                        {NAV_GROUPS.map(group => (
                                            <div key={group.label}>
                                                <p className="menu-label">{group.label}</p>
                                                <ul className="menu-list">
                                                    {group.items.map(item => {
                                                        const dot = getSidebarDot(item.id);
                                                        return (
                                                            <li key={item.id}>
                                                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                                                <a
                                                                    className={activeNav === item.id ? 'is-active' : ''}
                                                                    href="#"
                                                                    title={item.hint || undefined}
                                                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                                                    onClick={(e) => { e.preventDefault(); guardedNavigate(item.id); }}
                                                                >
                                                                    {item.label}
                                                                    {dot === 'done'    && <span style={{ color: '#257942', fontSize: '0.75rem' }}>✓</span>}
                                                                    {dot === 'partial' && <span style={{ color: '#f0a500', fontSize: '0.75rem' }}>◐</span>}
                                                                    {dot === 'empty'   && <span style={{ color: '#ccc',    fontSize: '0.75rem' }}>○</span>}
                                                                </a>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        ))}
                                    </nav>
                                    {/* Fix 5 — Sidebar legend at the bottom */}
                                    <div className="is-size-7 has-text-grey px-3 py-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <span className="mr-2">✓ complete</span>
                                        <span className="mr-2">◐ partial</span>
                                        <span>○ not started</span>
                                    </div>
                                </aside>
                                <div className="thor-content box">
                                    {renderSection()}
                                </div>
                            </div>
                        </>
                    )}
                </StateHandler>
            </Container>
            <ThorToast />
        </>
    );
};

export const ThorComponent = () => (
    <>
        <Login />
        <AuthenticatedTemplate>
            <ThorProvider>
                <ThorContent />
            </ThorProvider>
        </AuthenticatedTemplate>
    </>
);

export default ThorComponent;
